<template>
<div class="container">
    <Breadcrumbs :here="branchName"
                 :ancestries="ancestries"/>
    <div class="row my-2 justify-content-sm-center justify-content-md-end">
        <button class="m-1 col-10 col-md-3 btn btn-info"
                :style="{'font-size': FontSizes.normal}"
                @click="onClickBranchApprovalList">
            <span class="wrap-text">{{ branchName }}支店の請求承認メッセージ一覧</span>
        </button>
        <button class="m-1 col-10 col-md-3 btn btn-info"
                :style="{'font-size': FontSizes.normal}"
                @click="onClickBranchDiffList">
            <span class="wrap-text">{{ branchName }}支店のデータ差分概要</span>
        </button>
        <div class="m-1 col-10 col-md-3 zip-download-button-wrap p-0">
            <button class="btn btn-info zip-download-button"
                    :style="{'font-size': FontSizes.normal}"
                    @click="onClickDownloadBranchInvoicePDFZip"
                    :disabled="!canClickDownloadZipButton()">
                <span class="wrap-text">{{ downloadZipButtonText() }}</span>
            </button>
            <div v-show="downloadingState === zipDownloadState.DOWNLOADING"
                 class="dl-progress-bar bg-info"
                 :style="{width: progressWidth()}"></div>
        </div>
    </div>
    <div class="row">
        <div class="col h2"
             :style="{ fontSize: FontSizes.large1}">{{ branchName }}支店のスタッフ一覧</div>
    </div>

    <div class="row justify-content-between mb-2 content-center">
        <div class="col-md-4 col-sm">
            <ClearableInput v-model:text="searchText"/>
        </div>
        <select class="option-select col-md-3 col-4 align-self-center" v-model="activeFlag">
            <option value="0">全アクティブユーザー</option>
            <option value="1">未登録ユーザー</option>
            <option value="2">登録済ユーザー</option>
        </select>
        <!-- <label class="switch">
            <input default="false" type="checkbox" v-model="activeFlag">
            <span class="slider round"></span>
        </label> -->
    </div>
    <div class="row">
        <div class="col staff-table-wrap container">
            <table class="table">
                <thead>
                    <tr>
                        <th>社員番号</th>
                        <th>名前</th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="staff in searchedStaffs"
                         :key="staff.vForKey">
                        <td>{{staff.staffId}}</td>
                        <td>{{staff.displayName}}</td>
                        <td>
                            <button class="btn btn-outline-info"
                                    :style="{'font-size': FontSizes.normal}"
                                    @click="onClickStaffDetail(staff.staffId)">スタッフ詳細へ</button>
                        </td>
                        <td>
                            <MenuButton :data="staff"
                                        :on-click="onClickStaffMenu"
                                        :key-as-id="staff.vForKey"/>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
    <PopupMenu :show="showStaffMenuPopup"
               :hide="hideStaffMenuPopup"
               :position="staffMenuPopupPos"
               :items="staffMenuItems"/>
</div>
</template>

<script lang="ts">
import {defineComponent, onMounted, onUnmounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {backendApi} from "@/utils/backend-api";
import {Staff} from "@/data-models/staff";
import {dateFormat, logoutMessages, subPaths} from "@/utils/consts";
import {loadingOverlayStateStore} from "@/stores/loading-overlay-state-store";
import ClearableInput from "@/components/ClearableInput.vue";
import {FontSizes} from "@/utils/font-sizes";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import PopupMenu, {popupMenuItemType, popupPositionType} from "@/components/PopupMenu.vue";
import MenuButton from "@/components/MenuButton.vue";
import {confirmDialogStateStore} from "@/stores/confirm-dialog-state-store";
import {toastStore} from "@/stores/toast-store";
import {InvoiceProcessStatus, invoiceProcessStatuses} from "@/data-models/invoice-process-status";
import {format} from "date-fns";
import {InvoiceStatusChecker} from "@/utils/invoice-status-checker";
import {InvoiceZipDownloader, zipDownloadState} from "@/utils/invoice-zip-downloader";
import {toPercent} from "@/utils/number-utils";

const staffPopupMenuItemIds = {
    RESET_STAFF_PASSWORD: "staff_popup_menu_item__reset_staff_password"
}

export default defineComponent({
    name: "Branch",
    components: {MenuButton, PopupMenu, Breadcrumbs, ClearableInput},
    setup() {

        const route = useRoute(),
            router = useRouter()

        let staffs = [] as Staff[]

        const searchedStaffs = ref<Staff[]>([] as Staff[]),
            branchName = ref<string>(""),
            searchText = ref<string>(""),
            showStaffMenuPopup = ref<boolean>(false),
            staffMenuPopupPos = ref<popupPositionType>({x: 0, y: 0}),
            staffMenuItems = ref<popupMenuItemType[]>([] as popupMenuItemType[]),
            downloadingState = ref<string>(zipDownloadState.OUT_OF_PROCESS),
            downloadingRatio = ref<number>(0)
            
        let activeFlag = ref<string>("0")

        const onClickStaffDetail = (staffId: number) => {
            loadingOverlayStateStore.commit("switchLoadingState", {
                show: true,
                message: "スタッフページへ移動中…"
            })
            router.push({
                path: subPaths.MY_PAGE,
                query: {
                    staff_id: staffId
                }
            })
        }

        const updateSearchedStaffs = (searchText: string) => {
            searchedStaffs.value.splice(0)
            staffs.forEach(staff => {
                if (searchText.length === 0 || staff.stringForSearch.includes(searchText)) {
                    searchedStaffs.value.push(staff)
                }
            })
        }

        const onClickBranchApprovalList = () => {
            loadingOverlayStateStore.commit("switchLoadingState", {
                show: true,
                message: `${branchName.value}支店の請求承認メッセージ一覧へ移動中…`
            })
            router.push({
                path: subPaths.BRANCH_PAGE_APPROVAL_LIST,
                query: {
                    name: branchName.value
                }
            })
        }

        const onClickBranchDiffList = () => {
            loadingOverlayStateStore.commit("switchLoadingState", {
                show: true,
                message: `${branchName.value}支店のデータ差分概要へ移動中…`
            })
            router.push({
                path: subPaths.POSTING_RECORDS_BRANCH_DIFF,
                query: {
                    name: branchName.value
                }
            })
        }

        watch(() => searchText.value, () => {
            updateSearchedStaffs(searchText.value)
        })

        watch(() => activeFlag.value, () => {
            inquireStaffListBackend()
        })

        const invoiceStatusChecker = new InvoiceStatusChecker()

        invoiceStatusChecker.onProcessStatus = ivProcess => {
            // console.log(ivProcess)
            invoiceProcess.value = ivProcess
        }

        invoiceStatusChecker.onError = async msg => {
            console.warn(msg)
            await router.push({
                path: subPaths.LOGGED_OUT,
                query: {
                    logout_message: logoutMessages.TOKEN_EXPIRED.key
                }
            })
        }

        let downloader: InvoiceZipDownloader|null = null

        const inquireStaffListBackend = async () => {
            const branchNameQuery = route.query.name as string
            if (branchNameQuery) {
                branchName.value = branchNameQuery
                const resp = await backendApi.inquireStaffList(branchNameQuery, Number(activeFlag.value))
                // console.log(resp)
                if (resp.isAuthorized) {
                    staffs = Staff.fromJsonArrToArr(resp.value)
                    updateSearchedStaffs("")

                    invoiceStatusChecker.start()

                } else {
                    await router.push(subPaths.LOGIN)
                }
            }
        }

        onMounted(async () => {
            await inquireStaffListBackend()
            loadingOverlayStateStore.commit("fadeout")
        })

        onUnmounted(() => {
            invoiceStatusChecker.stop()
            if (downloader) {
                downloader.stop()
            }
        })

        const onClickDownloadBranchInvoicePDFZip = async () => {

            // console.log("onClickDownloadBranchInvoicePDFZip")

            invoiceStatusChecker.stop()

            downloader = new InvoiceZipDownloader()

            downloader.onError = msg => {
                console.warn(msg)
            }

            downloader.start((msg, dlRatio) => {
                // console.log(msg, dlRatio)
                downloadingState.value = msg
                downloadingRatio.value = dlRatio

                if (downloadingState.value === zipDownloadState.FILE_NOT_FOUND) {
                    toastStore.commit("post", {
                        message: `${branchName.value}支店の請求書ZIPファイルは存在しません。`,
                        width: 300,
                    })
                }

            }, branchName.value).then(() => {
                downloadingState.value = zipDownloadState.OUT_OF_PROCESS
            })

            invoiceStatusChecker.start()
        }

        const onClickStaffMenu = (staff: Staff, btnDiv: HTMLDivElement) => {

            const divRect = btnDiv.getBoundingClientRect()
            staffMenuPopupPos.value = {
                x: divRect.x,
                y: divRect.y
            }

            console.log(staffMenuPopupPos.value, window.scrollY)

            staffMenuItems.value.splice(0)
            staffMenuItems.value.push({
                itemId: staffPopupMenuItemIds.RESET_STAFF_PASSWORD,
                text: "パスワードをリセット",
                data: staff,
                cb: (itemId: string, staff: Staff) => {
                    if (itemId === staffPopupMenuItemIds.RESET_STAFF_PASSWORD) {
                        confirmDialogStateStore.commit("switchConfirmDialog", {
                            show: true,
                            title: "パスワードのリセット",
                            message: `${staff.displayName}さんのパスワードをリセットして、社員IDと同じにしますか？`,
                            affirmativeText: "リセット",
                            affirmCallback: async (affirmed: boolean) => {
                                if (affirmed) {
                                    const resetResp = await backendApi.inquireResetPassword(staff.staffId.toString())
                                    if (resetResp.isAuthorized) {
                                        if (resetResp.isSuccessful) {
                                            toastStore.commit("post", {
                                                message: "パスワードをリセットしました。"
                                            })
                                        } else {
                                            toastStore.commit("post", {
                                                message: "パスワードのリセットに失敗しました。"
                                            })
                                        }
                                    } else {
                                        await router.push({
                                            path: subPaths.LOGGED_OUT,
                                            query: {
                                                logout_message: logoutMessages.TOKEN_EXPIRED.key
                                            }
                                        })
                                    }

                                }
                            },
                        })
                    }
                }
            })

            showStaffMenuPopup.value = true
        }

        const hideStaffMenuPopup = () => {
            showStaffMenuPopup.value = false
        }

        const invoiceProcess = ref<InvoiceProcessStatus>(new InvoiceProcessStatus())

        const downloadZipButtonText = (): string => {
            let txt = "支店の全請求書ZIPファイル"

            if (downloadingState.value === zipDownloadState.DOWNLOADING) {
                txt = `${branchName.value}支店の全請求書ZIPファイルダウンロード中 ${Math.round(toPercent(downloadingRatio.value))}%`
            } else {
                if (invoiceProcess.value.status === invoiceProcessStatuses.NOT_AVAILABLE) {
                    txt = `${branchName.value}支店の全請求書ZIPファイル 未作成`
                } else if (invoiceProcess.value.status === invoiceProcessStatuses.AVAILABLE) {
                    txt = `${branchName.value}支店の全請求書ZIPファイル［${format(invoiceProcess.value.createdAt as Date, dateFormat)}］作成`
                } else if (invoiceProcess.value.status === invoiceProcessStatuses.ON_PROCESS) {
                    txt = `${branchName.value}支店の全請求書ZIPファイル ${Math.round(toPercent(invoiceProcess.value.processRatio))}% 準備中...`
                }
            }
            return txt
        }

        const progressWidth = (): string => {
            return `${Math.round(downloadingRatio.value * 100)}%`
        }

        const canClickDownloadZipButton = (): boolean => {
            return invoiceProcess.value.status === invoiceProcessStatuses.AVAILABLE
                && downloadingState.value !== zipDownloadState.DOWNLOADING
        }

        return {
            branchName,
            onClickStaffDetail,
            searchedStaffs,
            searchText,
            FontSizes,
            ancestries: [
                {
                    name: "ホーム",
                    pathObj: subPaths.HOME
                }
            ],
            onClickBranchApprovalList,
            onClickDownloadBranchInvoicePDFZip,
            onClickStaffMenu,
            showStaffMenuPopup,
            hideStaffMenuPopup,
            staffMenuPopupPos,
            staffMenuItems,
            downloadZipButtonText,
            canClickDownloadZipButton,
            progressWidth,
            downloadingState,
            zipDownloadState,
            onClickBranchDiffList,
            activeFlag,
            inquireStaffListBackend,
        }
    }
})
</script>

<style scoped lang="scss">
.option-select {
    font-size: 1.25rem;
    height: 40px;
    margin: 0px 15px;
}
.staff-table-wrap {

    //height: calc(100vh - 150px);
    overflow-y: auto;
    //background-color: red;

    .table {

        thead th {
            position: sticky;
            top: 0;
            z-index: 1;
            background-color: whitesmoke;
            box-shadow: 1px 1px 3px #AAA;
        }

        button {
            //font-size: 1.5vw;
        }


    }
}

.btn {
    height: 40px;
}

.zip-download-button-wrap {
    position: relative;
    height: 40px;

    .zip-download-button {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
    }

    .dl-progress-bar {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        opacity: 50%;

        transition: width 0.3s ease;
        border-radius: 5px;
    }
}

</style>